import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Search, CheckCircle2, Clock, XCircle, Package, Phone, Smartphone, Sparkles, Ticket } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/apiClient";

const STAGES = [
  { k: "pending_review", l: "WISH RECEIVED", d: "Your wish is safely in our jar." },
  { k: "under_review", l: "UNDER REVIEW", d: "The Pop Panda crew is reading it." },
  { k: "sourcing", l: "HUNTING FOR IT", d: "We're checking suppliers & availability." },
  { k: "ordered", l: "IT'S ON THE WAY", d: "Ordered! Now we wait for the delivery truck." },
  { k: "available_in_store", l: "ON OUR SHELVES!", d: "Come grab it before it melts away!" },
];
const TERMINAL = {
  not_available: { l: "COULDN'T FIND IT", d: "We searched everywhere but couldn't source this one. So sorry!" },
  rejected: { l: "REQUEST CLOSED", d: "This request didn't make it through review." },
  cancelled: { l: "CANCELLED", d: "This request was cancelled." },
};

export default function TrackRequest() {
  const prefersReduced = useReducedMotion();
  const [params] = useSearchParams();
  const [savedRequests, setSavedRequests] = useState([]);
  const [results, setResults] = useState([]);
  const [codeInput, setCodeInput] = useState("");
  const [mobileInput, setMobileInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try { setSavedRequests(JSON.parse(localStorage.getItem("pp_my_requests") || "[]")); } catch { /* ignore */ }
    const code = params.get("code");
    if (code) { setCodeInput(code); lookupByCode(code); return; }
    const ref = params.get("ref"); const token = params.get("token");
    if (ref && token) lookupLegacy(ref, token);
    // eslint-disable-next-line
  }, []);

  const lookupByCode = async (code) => {
    const norm = (code || "").toUpperCase().trim();
    if (norm.length < 8) return toast.error("Enter your full tracking number, e.g. PP-WISH-7K4M9X");
    setLoading(true);
    try {
      const { data } = await api.get(`/wishlist/track-code/${encodeURIComponent(norm)}`);
      setResults([data]);
      saveLocal(data);
    } catch (e) { setResults([]); toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const lookupLegacy = async (ref, token) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/wishlist/track/${encodeURIComponent(ref)}`, { params: { token } });
      setResults([data]);
      saveLocal(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const lookupSaved = async (s) => {
    if (s.code) return lookupByCode(s.code);
    if (s.token && s.ref) return lookupLegacy(s.ref, s.token);
    toast.error("This saved entry is missing its tracker — use your mobile number below.");
  };

  const lookupByMobile = async () => {
    const digits = mobileInput.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) return toast.error("Enter a valid 10-digit Indian mobile number");
    setLoading(true);
    try {
      const { data } = await api.post("/wishlist/lookup-mobile", { mobile: digits });
      setResults(data.items);
      data.items.forEach(saveLocal);
      if (data.items.length === 0) toast.info("No wishes found for this number yet");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const saveLocal = (r) => {
    try {
      const stored = JSON.parse(localStorage.getItem("pp_my_requests") || "[]");
      const entry = { ref: r.public_ref, code: r.tracking_code, name: r.submitted_name, at: r.created_at };
      const updated = [entry, ...stored.filter(x => x.ref !== r.public_ref)].slice(0, 10);
      localStorage.setItem("pp_my_requests", JSON.stringify(updated));
      setSavedRequests(updated);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-[#FEED02] relative overflow-hidden" data-testid="track-page">
      {!prefersReduced && <FloatingBg />}
      <div className="relative z-10 max-w-2xl mx-auto px-4 py-10">
        <Link to="/request-product" className="font-type text-xs text-black/70 hover:text-black inline-flex items-center gap-1 mb-6"><ArrowLeft size={12} /> BACK</Link>
        <div className="inline-block px-3 py-1 rounded-full bg-black text-[#FEED02] font-type text-[10px] tracking-[0.25em] mb-4">POP PANDA · WISH TRACKER</div>
        <h1 className="font-type text-black leading-[0.95]" style={{ fontSize: "clamp(38px, 9vw, 68px)" }}>
          WHERE'S<br />YOUR WISH AT?
        </h1>
        <p className="mt-3 text-base text-black/70 max-w-md">Your favourite might be one step closer. Let's check.</p>

        {/* Two tracking methods */}
        <div className="mt-8 grid sm:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
          {/* Card 1 — tracking number */}
          <section className="bg-white rounded-3xl border-4 border-black p-5 shadow-[6px_6px_0_rgba(0,0,0,0.9)]" data-testid="track-by-code-card">
            <div className="flex items-center gap-2 mb-1">
              <Ticket size={16} />
              <div className="font-type text-xs tracking-[0.2em]">GOT YOUR TRACKER?</div>
            </div>
            <p className="text-xs text-black/60 mb-3">Enter the code you saved when you made your wish.</p>
            <input
              value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && lookupByCode(codeInput)}
              placeholder="PP-WISH-XXXXXX" maxLength={16}
              className="w-full px-4 py-3 border-2 border-black/20 focus:border-black rounded-xl outline-none font-type text-lg tracking-[0.12em] text-center"
              data-testid="track-code-input" />
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => lookupByCode(codeInput)} disabled={loading}
              className="mt-3 w-full bg-black text-[#FEED02] font-type text-sm py-3 rounded-xl disabled:opacity-50" data-testid="track-code-submit">
              {loading ? "…" : "FIND MY WISH →"}
            </motion.button>
          </section>

          <div className="grid place-items-center">
            <span className="font-type text-sm bg-black text-[#FEED02] px-3 py-1.5 rounded-full rotate-0 sm:-rotate-90">OR</span>
          </div>

          {/* Card 2 — mobile number */}
          <section className="bg-white rounded-3xl border-4 border-black p-5 shadow-[6px_6px_0_rgba(0,0,0,0.9)]" data-testid="track-by-mobile-card">
            <div className="flex items-center gap-2 mb-1">
              <Phone size={16} />
              <div className="font-type text-xs tracking-[0.2em]">SHARED YOUR NUMBER?</div>
            </div>
            <p className="text-xs text-black/60 mb-3">Use the mobile number you registered with.</p>
            <div className="flex items-stretch gap-2">
              <span className="shrink-0 px-2.5 flex items-center rounded-xl font-mono font-bold bg-black text-[#FEED02] border-2 border-black text-sm">+91</span>
              <input
                type="tel" inputMode="numeric" value={mobileInput} onChange={(e) => setMobileInput(e.target.value.replace(/[^\d\s]/g, "").slice(0, 14))}
                onKeyDown={(e) => e.key === "Enter" && lookupByMobile()}
                placeholder="98765 43210" maxLength={14}
                className="flex-1 min-w-0 px-3 py-3 border-2 border-black/20 focus:border-black rounded-xl outline-none font-mono"
                data-testid="mobile-lookup-input" />
            </div>
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={lookupByMobile} disabled={loading}
              className="mt-3 w-full bg-black text-[#FEED02] font-type text-sm py-3 rounded-xl disabled:opacity-50" data-testid="mobile-lookup-submit">
              {loading ? "…" : "FIND MY REQUESTS →"}
            </motion.button>
          </section>
        </div>

        {/* Saved on this device */}
        {savedRequests.length > 0 && (
          <section className="mt-6 bg-white rounded-3xl border-4 border-black p-5 shadow-[6px_6px_0_rgba(0,0,0,0.9)]" data-testid="saved-requests-card">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone size={16} />
              <div className="font-type text-xs tracking-[0.2em]">SAVED ON THIS DEVICE</div>
            </div>
            <div className="space-y-2">
              {savedRequests.map((s) => (
                <motion.button key={s.ref} whileHover={{ x: 3 }} onClick={() => lookupSaved(s)}
                  className="w-full text-left p-3 rounded-xl bg-[#FEED02]/40 hover:bg-[#FEED02]/70 border-2 border-black flex items-center justify-between gap-3 transition"
                  data-testid={`saved-req-${s.ref}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-sm text-black truncate">{s.name || s.ref}</div>
                    <div className="font-type text-[11px] text-black/55">{s.code || s.ref}</div>
                  </div>
                  <span className="shrink-0 bg-black text-[#FEED02] font-type text-[10px] tracking-widest px-2.5 py-1 rounded-full">TRACK →</span>
                </motion.button>
              ))}
            </div>
          </section>
        )}

        {/* Results */}
        <AnimatePresence>
          {results.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-4" data-testid="track-result">
              <div className="font-type text-xs tracking-[0.2em] text-black/70">FOUND {results.length} WISH{results.length === 1 ? "" : "ES"}</div>
              {results.map((r) => <RequestCard key={r.public_ref} r={r} />)}
            </motion.section>
          )}
        </AnimatePresence>

        {savedRequests.length === 0 && results.length === 0 && (
          <div className="mt-8 bg-white rounded-3xl border-4 border-black p-6 text-center shadow-[6px_6px_0_rgba(0,0,0,0.9)]">
            <Sparkles size={28} className="mx-auto mb-2" />
            <div className="font-type text-xl mb-1">NO WISHES YET?</div>
            <div className="text-sm text-black/65 mb-4">Make your first wish — you'll get a tracker code right away.</div>
            <Link to="/request-product" className="inline-flex items-center gap-2 bg-black text-[#FEED02] font-type text-xs px-5 py-3 rounded-full">
              REQUEST A PRODUCT <Sparkles size={13} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function RequestCard({ r }) {
  const terminal = TERMINAL[r.status];
  const imgUrl = r.canonical_product?.image_path ? `${API_BASE}/wishlist/images/${r.canonical_product.image_path}` : null;
  return (
    <div className="bg-white rounded-3xl border-4 border-black p-5 shadow-[6px_6px_0_rgba(0,0,0,0.9)]">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 rounded-xl bg-[#FEED02] grid place-items-center overflow-hidden shrink-0 border-2 border-black">
          {imgUrl ? <img src={imgUrl} alt="" className="w-full h-full object-cover" /> : <Package size={28} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-type text-[11px] tracking-[0.15em] text-black/55">{r.tracking_code || r.public_ref}</div>
          <div className="font-display text-xl font-black leading-tight truncate">{r.submitted_name}</div>
          <div className="text-xs text-black/60 truncate">{[r.submitted_brand, r.submitted_variant, r.submitted_category].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      {terminal ? (
        <div className="rounded-xl bg-slate-100 border-2 border-black/10 p-3 text-sm" data-testid={`status-${r.status}`}>
          <div className="flex items-center gap-2 font-type"><XCircle size={16} /> <strong>{terminal.l}</strong></div>
          <div className="text-black/60 mt-1">{terminal.d}{r.customer_update ? ` — ${r.customer_update}` : ""}</div>
        </div>
      ) : (
        <StatusTimeline current={r.status} />
      )}
      {r.customer_update && !terminal && (
        <div className="mt-4 rounded-xl bg-[#FEED02]/40 border-2 border-black p-3">
          <div className="font-type text-[10px] tracking-[0.2em] mb-1">NOTE FROM POP PANDA</div>
          <div className="text-sm">{r.customer_update}</div>
        </div>
      )}
      {r.canonical_product && (
        <Link to={`/requested-products/${r.canonical_product.slug}`} className="mt-4 inline-flex items-center gap-2 font-type text-xs text-black hover:underline" data-testid="track-canonical-link">
          <Package size={11} /> SEE IT ON THE WISHLIST &rarr;
        </Link>
      )}
      <div className="mt-3 font-type text-[10px] text-black/45 tracking-widest">WISHED {new Date(r.created_at).toLocaleDateString()} · UPDATED {new Date(r.updated_at).toLocaleDateString()}</div>
    </div>
  );
}

function StatusTimeline({ current }) {
  const mapCurrent = current === "approved" ? "under_review" : current;
  const idx = STAGES.findIndex((s) => s.k === mapCurrent);
  return (
    <ol className="space-y-2.5" data-testid="status-timeline">
      {STAGES.map((s, i) => {
        const done = i < idx; const active = i === idx;
        return (
          <li key={s.k} className="flex items-center gap-3">
            <motion.span
              initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.08 }}
              className={`w-8 h-8 rounded-full grid place-items-center shrink-0 font-type text-xs border-2 border-black ${done ? "bg-black text-[#FEED02]" : active ? "bg-[#FEED02] text-black" : "bg-white text-black/35"}`}>
              {done ? <CheckCircle2 size={14} /> : active ? <Clock size={14} /> : i + 1}
            </motion.span>
            <div className="min-w-0">
              <div className={`font-type text-sm ${active ? "text-black" : done ? "text-black/75" : "text-black/35"}`}>{s.l}</div>
              {active && <div className="text-[11px] text-black/60">{s.d}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function FloatingBg() {
  const items = ["🍭", "🍡", "🍦", "🍧", "🍨"];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 7 }).map((_, i) => (
        <motion.div key={i} className="absolute text-5xl opacity-20"
          style={{ left: `${(i * 17 + 8) % 90}%`, top: `${(i * 23 + 10) % 85}%` }}
          animate={{ y: [0, -20, 0], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}>
          {items[i % items.length]}
        </motion.div>
      ))}
    </div>
  );
}
